import React from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import {
    Sparkles, ArrowRight, ShieldCheck,
    Heart, Home as HomeIcon, Briefcase, Wallet, Brain, CloudRain, BatteryLow, MoreHorizontal,
    Compass, HeartHandshake, Languages, Play, Moon, Sun, Flower2, Leaf,
} from "lucide-react";

const HERO = "https://static.prod-images.emergentagent.com/jobs/a04d81c8-8ddd-47c0-93b4-fd8d9e3680be/images/cf2020cd1e55eefd6cda65419189b365487a3fec250ebc1262e625bf1e4a44bb.jpeg";
const PANEL = "https://static.prod-images.emergentagent.com/jobs/a04d81c8-8ddd-47c0-93b4-fd8d9e3680be/images/6314707fc1e04b152674aaffd3c59f3b406622ea5ab8ccebeb6a534f84573cbe.jpeg";

const GREEN = "#1F3A30";
const GOLD = "#C9A45C";
const HERO_BG = "#0F1F19";
const IVORY = "#F5F0E6";

const Eyebrow = ({ children }) => (
    <p className="inline-flex items-center gap-2 uppercase tracking-[0.24em] text-xs font-semibold" style={{ color: GOLD }}>
        <Sparkles size={14} strokeWidth={1.4} /> {children}
    </p>
);

const GoldIcon = ({ icon: Icon }) => (
    <span className="h-12 w-12 rounded-full flex items-center justify-center" style={{ border: `1px solid ${GOLD}66` }}>
        <Icon size={22} strokeWidth={1.3} style={{ color: GOLD }} />
    </span>
);

const HERO_FEATURES = [
    [Moon, "Mindfulness & Clarity"],
    [Sun, "Astrological Insights"],
    [Flower2, "Emotion Balance"],
    [HeartHandshake, "No Judgement"],
    [Leaf, "Personalised Guidance"],
];

const CONCERNS = [
    [Heart, "Relationships", "Distance, conflict or feeling unseen by someone close."],
    [HomeIcon, "Family", "Pressure, expectations or feeling unheard at home."],
    [Briefcase, "Career & studies", "Stress, criticism or fear of making mistakes."],
    [Wallet, "Money stress", "Uncertainty or responsibilities weighing on you."],
    [Brain, "Anxiety & overthinking", "A mind that keeps replaying and worrying."],
    [CloudRain, "Low mood", "Feeling flat, heavy or less interested lately."],
    [BatteryLow, "Burnout", "Exhausted and unable to recover your energy."],
    [MoreHorizontal, "Something else", "Your own situation, in your own words."],
];

const STEPS = [
    ["4 free questions", "Share your concern and how you feel — one gentle question at a time."],
    ["A free preview", "A short, personal reflection based only on your own answers."],
    ["Full 18-question reflection", "Unlock the complete reflection for ₹299 one-time via UPI."],
    ["Report + AI companion", "A personal report, then talk it through with a supportive companion."],
];

const WHY = [
    [ShieldCheck, "Private by design", "Your reflections belong to your account. Memory is off until you turn it on."],
    [Compass, "Built around your answers", "Every reflection uses what you actually said — never invented facts."],
    [HeartHandshake, "Gentle, not judgemental", "A calm space that meets you where you are, whoever you are."],
    [Languages, "Clear and simple", "A calm, plain-English experience from start to finish."],
];

export default function Home() {
    const { user, config } = useApp();
    const nav = useNavigate();
    const price = config?.price || 299;
    const start = () => nav(user ? "/assessment" : "/onboarding");
    const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

    return (
        <div style={{ color: "#242722" }}>
            {/* HERO */}
            <section className="max-w-7xl mx-auto px-3 sm:px-6 pt-3 sm:pt-5">
                <div data-testid="hero-section" className="relative overflow-hidden rounded-[1.75rem] sm:rounded-[2.25rem]" style={{ background: HERO_BG }}>
                    <div className="relative lg:hidden h-[300px] sm:h-[380px]">
                        <img src={HERO} alt="A stone archway opening onto a moonlit lake with mountains and the phases of the moon" className="w-full h-full object-cover" />
                        <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, ${HERO_BG}00 50%, ${HERO_BG} 100%)` }} />
                    </div>
                    <div className="hidden lg:block absolute inset-y-0 right-0 w-[56%]">
                        <img src={HERO} alt="" aria-hidden="true" className="w-full h-full object-cover object-center" />
                        <div className="absolute inset-0" style={{ background: `linear-gradient(90deg, ${HERO_BG} 0%, ${HERO_BG}CC 14%, ${HERO_BG}00 48%)` }} />
                        <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, ${HERO_BG}66 0%, ${HERO_BG}00 25%, ${HERO_BG}00 80%, ${HERO_BG}99 100%)` }} />
                    </div>

                    <div className="relative px-6 sm:px-10 lg:px-14 pb-10 pt-6 lg:pt-16 lg:pb-14 lg:min-h-[640px] flex flex-col justify-between">
                        <div className="lg:max-w-[46%]">
                            <p className="uppercase tracking-[0.26em] text-[11px] sm:text-xs font-semibold" style={{ color: GOLD }}>A space to pause, reflect and realign</p>
                            <h1 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.02]" style={{ color: IVORY }}>
                                Know Yourself.<br />Discover Your<br /><em className="font-serif italic" style={{ color: GOLD }}>Inner Self.</em>
                            </h1>
                            <p className="mt-6 text-base sm:text-lg leading-relaxed max-w-md" style={{ color: `${IVORY}D9` }}>
                                A guided self-reflection journey with psychology and astrology insights to help you find clarity, balance and become a better version of yourself.
                            </p>
                            <div className="mt-8 flex flex-wrap items-center gap-5">
                                <button data-testid="start-journey-button" onClick={start}
                                    className="inline-flex items-center gap-2 rounded-xl px-6 py-3.5 font-semibold text-sm transition hover:brightness-105"
                                    style={{ background: "#F0DDB0", color: "#24210F" }}>
                                    Start Your Journey <ArrowRight size={17} />
                                </button>
                                <button data-testid="how-it-works-button" onClick={() => scrollTo("how")}
                                    className="inline-flex items-center gap-3 text-sm font-medium" style={{ color: IVORY }}>
                                    <span className="h-9 w-9 rounded-full flex items-center justify-center" style={{ border: `1px solid ${GOLD}` }}>
                                        <Play size={13} fill={GOLD} style={{ color: GOLD, marginLeft: 1 }} />
                                    </span>
                                    How It Works
                                </button>
                            </div>
                            <p className="mt-5 text-xs" style={{ color: `${IVORY}80` }}>4 questions free · Full journey ₹{price} one-time · No subscription</p>
                        </div>

                        <div className="mt-12 lg:mt-14 lg:max-w-[50%]">
                            <div className="grid grid-cols-3 sm:grid-cols-5 gap-y-6 gap-x-4">
                                {HERO_FEATURES.map(([Icon, label]) => (
                                    <div key={label} data-testid={`hero-feature-${label}`} className="flex flex-col items-center sm:items-start text-center sm:text-left gap-2.5">
                                        <Icon size={28} strokeWidth={1.2} style={{ color: GOLD }} />
                                        <span className="text-[11px] sm:text-xs leading-snug" style={{ color: `${IVORY}CC` }}>{label}</span>
                                    </div>
                                ))}
                            </div>
                            <p className="mt-10 font-serif italic text-xl sm:text-2xl" style={{ color: `${IVORY}E6` }}>“A calmer mind. A clearer path. A brighter you.”</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* WHAT'S ON YOUR MIND */}
            <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16 lg:py-24">
                <Eyebrow>What's on your mind</Eyebrow>
                <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl max-w-2xl leading-tight" style={{ color: GREEN }}>Start wherever it feels most present.</h2>
                <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                    {CONCERNS.map(([Icon, title, desc]) => (
                        <button key={title} onClick={start} data-testid={`concern-card-${title}`}
                            className="text-left rounded-2xl bg-white p-6 hover:-translate-y-0.5 transition-transform"
                            style={{ border: `1px solid ${GREEN}14`, boxShadow: "0 6px 26px rgba(31,58,48,.05)" }}>
                            <GoldIcon icon={Icon} />
                            <h3 className="mt-4 font-serif text-xl" style={{ color: GREEN }}>{title}</h3>
                            <p className="mt-1 text-sm text-charcoal/70 leading-relaxed">{desc}</p>
                        </button>
                    ))}
                </div>
            </section>

            {/* HOW IT WORKS */}
            <section id="how" className="scroll-mt-20" style={{ background: "#EFE8D8", borderTop: `1px solid ${GREEN}14`, borderBottom: `1px solid ${GREEN}14` }}>
                <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 lg:py-24">
                    <Eyebrow>How it works</Eyebrow>
                    <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl max-w-2xl leading-tight" style={{ color: GREEN }}>A calm path, four simple steps.</h2>
                    <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {STEPS.map(([title, desc], i) => (
                            <div key={title} className="rounded-2xl bg-white p-7" style={{ border: `1px solid ${GREEN}14`, boxShadow: "0 6px 26px rgba(31,58,48,.05)" }}>
                                <span className="font-serif text-4xl" style={{ color: `${GOLD}` }}>0{i + 1}</span>
                                <h3 className="mt-3 font-serif text-xl" style={{ color: GREEN }}>{title}</h3>
                                <p className="mt-2 text-sm text-charcoal/70 leading-relaxed">{desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* WHY WHY-AM */}
            <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16 lg:py-24">
                <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                    <div>
                        <Eyebrow>Why WHY-AM</Eyebrow>
                        <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight" style={{ color: GREEN }}>Thoughtful, private, and genuinely yours.</h2>
                        <div className="mt-10 space-y-7">
                            {WHY.map(([Icon, title, desc]) => (
                                <div key={title} className="flex gap-4">
                                    <GoldIcon icon={Icon} />
                                    <div>
                                        <h3 className="font-serif text-xl" style={{ color: GREEN }}>{title}</h3>
                                        <p className="mt-1 text-sm text-charcoal/70 leading-relaxed">{desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="rounded-[2rem] overflow-hidden aspect-[3/4] max-h-[560px] mx-auto w-full" style={{ boxShadow: "0 24px 70px rgba(31,58,48,.25)" }}>
                        <img src={PANEL} alt="Golden moon phases and a lotus line motif on deep forest green" className="w-full h-full object-cover" />
                    </div>
                </div>
            </section>

            {/* CLOSING CTA */}
            <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-6">
                <div className="relative rounded-[2rem] overflow-hidden px-6 sm:px-14 py-16 lg:py-24 text-center" style={{ background: GREEN }}>
                    <img src={PANEL} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover opacity-20" />
                    <div className="relative">
                        <Sparkles size={24} strokeWidth={1.3} className="mx-auto" style={{ color: GOLD }} />
                        <h2 className="mt-5 font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight max-w-2xl mx-auto" style={{ color: "#F5F0E6" }}>Ready to understand yourself better?</h2>
                        <p className="mt-4 max-w-md mx-auto" style={{ color: "#F5F0E6B3" }}>Begin with four free questions. No pressure, no subscription.</p>
                        <button data-testid="pricing-start-button" onClick={start}
                            className="mt-8 inline-flex items-center gap-2 rounded-full px-8 py-3.5 font-semibold transition hover:brightness-105"
                            style={{ background: GOLD, color: "#24210F" }}>
                            Start Your Reflection <ArrowRight size={18} />
                        </button>
                        <p className="mt-6 text-xs" style={{ color: "#F5F0E680" }}>WHY-AM is a self-reflection tool — not therapy, diagnosis or emergency help.</p>
                    </div>
                </div>
            </section>
        </div>
    );
}
